
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export interface FeedbackRequest {
  id: string;
  user_id: string;
  candidate_name?: string;
  candidate_email?: string;
  candidate_user_id?: string;
  recruiter_name?: string;
  recruiter_email?: string;
  recruiter_company?: string;
  recruiter_user_id?: string;
  job_title?: string;
  message?: string;
  created_at: string;
  updated_at?: string;
  status?: string;
  unique_token?: string;
  feedback_id?: string;
  context?: string;
  direction?: string;
  last_reminder_sent?: string;
}

export const useFeedbackRequests = (type: 'sent' | 'received') => {
  const [requests, setRequests] = useState<FeedbackRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const isEntreprise = profile?.role === 'entreprise';
  const isCandidat = profile?.role === 'candidat';
  const isAdmin = profile?.role === 'admin';

  // Fonction pour rediriger vers le bon formulaire de demande
  const navigateToRequestForm = useCallback(() => {
    navigate('/request-feedback');
  }, [navigate]);

  const fetchRequests = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Récupération de toutes les demandes depuis les deux tables
      let combinedRequests: FeedbackRequest[] = [];
      
      // Déterminer strictement si on cherche les demandes envoyées ou reçues
      if (type === 'sent') {
        // Pour les demandes envoyées, uniquement celles où l'utilisateur est l'expéditeur
        const { data: c2rSentRequests, error: c2rSentError } = await supabase
          .from('candidate_to_recruiter_feedback_requests')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (c2rSentError) throw c2rSentError;
        if (c2rSentRequests) combinedRequests = [...combinedRequests, ...c2rSentRequests];

        // Aussi les demandes de la table legacy
        const { data: legacySentRequests, error: legacySentError } = await supabase
          .from('feedback_requests')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (legacySentError) throw legacySentError;
        if (legacySentRequests) combinedRequests = [...combinedRequests, ...legacySentRequests];
      } else {
        // Pour les demandes reçues, celles où l'utilisateur est le destinataire mais PAS l'expéditeur
        if (isCandidat) {
          // Le candidat reçoit des demandes où il est marqué comme destinataire
          const { data: c2rReceivedAsCandidate, error: candidateError } = await supabase
            .from('candidate_to_recruiter_feedback_requests')
            .select('*')
            .or(`candidate_email.eq.${user.email},candidate_user_id.eq.${user.id}`)
            .neq('user_id', user.id) // Exclure les demandes créées par l'utilisateur actuel
            .order('created_at', { ascending: false });
              
          if (candidateError) throw candidateError;
          if (c2rReceivedAsCandidate) combinedRequests = [...combinedRequests, ...c2rReceivedAsCandidate];
          
          // Aussi les demandes de la table legacy
          const { data: legacyReceivedAsCandidate, error: legacyCandidateError } = await supabase
            .from('feedback_requests')
            .select('*')
            .or(`candidate_email.eq.${user.email},candidate_user_id.eq.${user.id}`)
            .neq('user_id', user.id) // Exclure les demandes créées par l'utilisateur actuel
            .order('created_at', { ascending: false });
              
          if (legacyCandidateError) throw legacyCandidateError;
          if (legacyReceivedAsCandidate) combinedRequests = [...combinedRequests, ...legacyReceivedAsCandidate];
        }
        
        if (isEntreprise) {
          // Le recruteur reçoit des demandes où il est marqué comme destinataire
          const { data: c2rReceivedAsRecruiter, error: recruiterError } = await supabase
            .from('candidate_to_recruiter_feedback_requests')
            .select('*')
            .or(`recruiter_email.eq.${user.email},recruiter_user_id.eq.${user.id}`)
            .neq('user_id', user.id) // Exclure les demandes créées par l'utilisateur actuel
            .order('created_at', { ascending: false });
              
          if (recruiterError) throw recruiterError;
          if (c2rReceivedAsRecruiter) combinedRequests = [...combinedRequests, ...c2rReceivedAsRecruiter];
          
          // Aussi les demandes de la table legacy
          const { data: legacyReceivedAsRecruiter, error: legacyRecruiterError } = await supabase
            .from('feedback_requests')
            .select('*')
            .or(`recruiter_email.eq.${user.email},recruiter_user_id.eq.${user.id}`)
            .neq('user_id', user.id) // Exclure les demandes créées par l'utilisateur actuel
            .order('created_at', { ascending: false });
              
          if (legacyRecruiterError) throw legacyRecruiterError;
          if (legacyReceivedAsRecruiter) combinedRequests = [...combinedRequests, ...legacyReceivedAsRecruiter];
        }
      }
      
      // Éliminer les doublons (si le même ID apparaît dans les deux tables)
      const uniqueRequestsMap = new Map<string, FeedbackRequest>();
      combinedRequests.forEach(request => {
        uniqueRequestsMap.set(request.id, request);
      });
      
      // Convertir la Map en Array et trier par date de création
      const uniqueRequests = Array.from(uniqueRequestsMap.values());
      uniqueRequests.sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      
      setRequests(uniqueRequests);
    } catch (error) {
      console.error('Error fetching feedback requests:', error);
    } finally {
      setLoading(false);
    }
  }, [user, type, isCandidat, isEntreprise]);
  
  // Charger les données au démarrage
  useEffect(() => {
    if (user) {
      fetchRequests();
    }
  }, [user, fetchRequests]);

  return {
    requests,
    loading,
    isEntreprise,
    isCandidat,
    isAdmin,
    fetchRequests,
    navigateToRequestForm
  };
};
